import type { LlmStreamMetadataDto, MissionAssessmentInterAiContextDto } from '@@types/api.types';
import { MissionAssessmentFieldName } from '@@types/api.types';
import { GenerateButton } from '@components/shared/generate-button.component';
import { useGetTechnology } from '@queries/technology.query';
import React from 'react';
import { MISSION_ASSESSMENT_AI_ENDPOINTS } from '../../../../constants/mission-assessment-endpoints.constants';
import { MISSION_ASSESSMENT_PROMPT_GUIDELINES } from '../../../../constants/prompts.constants';

type MissionAssessmentGenerateButtonProps = {
  className?: string;
  disabled?: boolean;
  effectContext?: {
    effectId?: string;
    secondaryEffectIds?: string[];
  };
  fieldName: MissionAssessmentFieldName;
  /**
   * Row context for interoperability/interchangeability fields so drafts target
   * the specific counterpart system instead of a generic relationship.
   */
  interContext?: MissionAssessmentInterAiContextDto;
  /**
   * Accept a Suggestion from the fullscreen chat workspace: writes the accepted
   * draft content into the target form field.
   */
  onAccept?: (value: string) => void;
  onMetadata?: (metadata: LlmStreamMetadataDto) => void;
  onStream: (value: string) => void;
  onStreamingStart?: () => void;
  onStreamingEnd?: () => void;
  placeholder?: string;
  technologyId: string;
};

export const MissionAssessmentGenerateButton: React.FC<MissionAssessmentGenerateButtonProps> = (props) => {
  const {
    className,
    disabled = false,
    effectContext,
    fieldName,
    interContext,
    onAccept,
    onMetadata,
    onStream,
    onStreamingStart,
    onStreamingEnd,
    placeholder,
    technologyId,
  } = props;
  const information = MISSION_ASSESSMENT_PROMPT_GUIDELINES[fieldName];

  const { data: technology } = useGetTechnology(technologyId);

  const description = (
    <>
      Use Tyr's AI assistant to help with your evalution of <b>{technology?.name || 'this technology'}</b>. All relevant
      metadata will be used by the AI assistant. If you'd like to add additional context or prompts, use the input
      below.
    </>
  );

  return (
    <GenerateButton
      apiEndpoint={MISSION_ASSESSMENT_AI_ENDPOINTS.generate}
      chatApiEndpoint={MISSION_ASSESSMENT_AI_ENDPOINTS.chat}
      className={className}
      data={{
        ...(effectContext?.effectId || effectContext?.secondaryEffectIds?.length ? { effectContext } : {}),
        ...(interContext?.name || interContext?.reference ? { interContext } : {}),
        fieldName,
        technologyId,
      }}
      description={description}
      disabled={disabled}
      information={information}
      onAccept={onAccept}
      onMetadata={onMetadata}
      onStream={onStream}
      onStreamingStart={onStreamingStart}
      onStreamingEnd={onStreamingEnd}
      placeholder="(Optional) Additional context or prompts"
      title={information?.title}
    />
  );
};
